import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { baseUrlApiManagement, currentVersionManagement } from './data/axios-base';
import FrontendParams from '../models/general/frontend-params';

let app;
let FCM_VAPID_KEY: string;

const fetchFrontendConfigs = async () => {
  try {
    const response = await fetch(
      `${baseUrlApiManagement}/${currentVersionManagement}/frontend-params/`,
    ); // Replace with your backend endpoint
    if (!response.ok) throw new Error('Failed to fetch Firebase configuration');
    const config: FrontendParams = await response.json();
    return config; // Expect config to contain { firebaseConfig, FCM_VAPID_KEY }
  } catch (error) {
    console.error('Error fetching Firebase configuration:', error);
    return null;
  }
};

const initializeFirebase = async () => {
  const config = await fetchFrontendConfigs();
  if (config) {
    app = initializeApp(config.firebaseConfigs);
    FCM_VAPID_KEY = config.firebaseFcmVapidKey;
  } else {
    throw new Error('Failed to initialize Firebase: Configuration missing');
  }
};

export const messaging = (async () => {
  try {
    await initializeFirebase();
    const isSupportedBrowser = await isSupported();
    if (isSupportedBrowser) {
      return getMessaging(app);
    }
    console.log(
      'Firebase is not supported in this browser and therefore it will not receive notifications',
    );
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
})();

export const getOrRegisterServiceWorker = () => {
  try {
    if ('serviceWorker' in navigator && typeof window.navigator.serviceWorker !== 'undefined') {
      return window.navigator.serviceWorker
        .getRegistration('/firebase-push-notification-scope')
        .then((serviceWorker) => {
          if (serviceWorker) return serviceWorker;
          return window.navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/firebase-push-notification-scope',
          });
        });
    }
    throw new Error('The browser doesn`t support service worker.');
  } catch (error) {
    console.error('Failed to get or register service worker');
  }
};

const getNativePushToken = (): Promise<string | undefined> => {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: string | undefined) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    PushNotifications.addListener('registration', (token) => {
      settle(token.value);
    }).catch((error) => {
      console.error('Failed to listen for native push registration: ', error);
      settle(undefined);
    });
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Failed to register for native push notifications: ', error);
      settle(undefined);
    }).catch((error) => {
      console.error('Failed to listen for native push registration errors: ', error);
      settle(undefined);
    });

    PushNotifications.checkPermissions()
      .then((status) =>
        status.receive === 'granted' ? status : PushNotifications.requestPermissions(),
      )
      .then((result) => {
        if (result.receive !== 'granted') {
          settle(undefined);
          return undefined;
        }
        // The "registration" listener above carries the token; register() only
        // starts the request.
        return PushNotifications.register();
      })
      .catch((error) => {
        console.error('Failed to request native push notifications: ', error);
        settle(undefined);
      });
  });
};

export const getFirebaseToken = async () => {
  if (Capacitor.isNativePlatform()) {
    return getNativePushToken();
  }

  try {
    await initializeFirebase();
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const messagingResolve = await messaging;
      if (messagingResolve) {
        return getOrRegisterServiceWorker()?.then((serviceWorkerRegistration) => {
          return Promise.resolve(
            getToken(messagingResolve, {
              vapidKey: FCM_VAPID_KEY,
              serviceWorkerRegistration,
            }),
          ).catch((error) => {
            console.error('Failed to get token: ' + error);
            return undefined;
          });
        });
      }
    } else {
    }
  } catch (error) {
    console.log('An error occurred while retrieving token. ', error);
  }
};
